import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Swiper, SwiperSlide } from 'swiper/react';
import { EffectCoverflow, Autoplay, Pagination, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/effect-coverflow';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import {
  Box,
  Flex,
  Text,
  Button,
  VStack,
  HStack,
  Progress,
  SimpleGrid,
  Container,
  Heading,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Badge,
  Image,
  Icon,
  NumberInput,
  NumberInputField,
  Input,
  Checkbox,
  useBreakpointValue,
  Spinner,
  IconButton,
} from "@chakra-ui/react";
import {
  FaHeart,
  FaShieldAlt,
  FaReceipt,
  FaArrowUp,
  FaWhatsapp,
  FaCopy,
  FaCheck,
} from "react-icons/fa";
import { FaLocationDot } from "react-icons/fa6";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import CountUp from "react-countup";
import { formatCurrency, calculatePercentage } from "../Components/utils";
import { BE_API_URL } from "../constants/homePage";
import { postPaymentFormData } from "../Redux/clientSlices/clientUsers";
import Fotter from "../Components/HomePageComponents/Fotter";
import PaymentModal from "../Components/HomePageComponents/PaymentModal";
import { themeColor } from "../Components/utils";
import logo from "../assets/logo.png";
import qrCode from "../assets/images/qr.jpeg";
import project from "../assets/images/project.jpeg";
import update1 from "../assets/images/update1.jpeg";
import update2 from "../assets/images/update2.jpeg";
import update3 from "../assets/images/update3.jpeg";
import update4 from "../assets/images/update4.jpeg";
import update5 from "../assets/images/update5.jpeg";
import update6 from "../assets/images/update6.jpeg";
import donor1 from "../assets/donor/1.jpeg";
import donor2 from "../assets/donor/2.jpeg";
import donor3 from "../assets/donor/3.jpeg";
import donor4 from "../assets/donor/4.jpeg";
import donor5 from "../assets/donor/5.jpeg";

const MotionBox = motion(Box);

const CAMPAIGN_ID = 800;
const PRICE_PER_SQFT = 2100;

const palette = {
  pageBg: "#FFFBF7",
  surface: "#FFFFFF",
  surfaceMuted: "#F5EFE6",
  border: "#E8DFD0",
  text: "#2C1810",
  textMuted: "#6B5E52",
  saffron: "#E8751A",
  saffronDark: "#C45F0E",
  gold: "#B8860B",
  goldLight: "#F4E4BC",
  maroon: "#7B2D26",
  heroGradient: "linear(135deg, #FFF8F0 0%, #FFE8CC 45%, #F5EFE6 100%)",
};

const faqs = [
  {
    question: "What is Square Feet Seva?",
    answer:
      "Square Feet Seva lets you sponsor the construction of ISKCON Mangalore’s Sri Radha Krishna Temple at Govardhana Hills by contributing per square foot of built area. Each square foot is ₹2,100, creating a tangible link between your seva and the mandir rising for Lord Krishna.",
  },
  {
    question: "How is my donation used?",
    answer:
      "Funds go directly toward Phase 1 temple construction—materials, craftsmanship, and infrastructure on the 11-acre Govardhana Hills campus. We share periodic photo and video updates so you can see your square feet take form.",
  },
  {
    question: "Do I get tax benefits?",
    answer:
      "Yes. Donations qualify for exemption under Section 80G of the Income Tax Act when you provide valid PAN details at checkout.",
  },
  {
    question: "Can I dedicate seva in someone’s name?",
    answer:
      "Yes. You may dedicate square feet in memory or honor of loved ones. Names appear on our Wall of Gratitude according to your seva tier.",
  },
  {
    question: "What is the minimum contribution?",
    answer: "Minimum sponsorship is 1 square foot (₹2,100). You may also enter any custom number of square feet above that.",
  },
];


const SqftSevaCampaign = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isMobile = useBreakpointValue({ base: true, md: false });

  const [campaign, setCampaign] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [customSqft, setCustomSqft] = useState("1");
  const [selectedQuickSqft, setSelectedQuickSqft] = useState(3);
  const [isCustomMode, setIsCustomMode] = useState(false);
  const [paymentModel, setPaymentModel] = useState(false);
  const [amount] = useState(null);
  const [errors, setErrors] = useState({});
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [copiedItem, setCopiedItem] = useState(null);
  const [formData, setFormData] = useState({
    email: "",
    mobileno: "",
    country: "Indian",
    pincode: "",
    panno: "",
    address: "",
    amount: null,
    username: "",
    campaignsid: String(CAMPAIGN_ID),
    prasadRequired: false,
    taxExemption: false,
    isanonymous: false,
  });

  const fetchCampaign = useCallback(async () => {
    try {
      const response = await fetch(`${BE_API_URL}/campaign/${CAMPAIGN_ID}`);
      const data = await response.json();
      if (response.ok) {
        setCampaign(data);
      }
    } catch (error) {
      console.error("Error fetching campaign:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCampaign();
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [fetchCampaign]);

  useEffect(() => {
    const onScroll = () => setShowScrollButton(window.scrollY > 400);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const raisedAmount = campaign?.raisedFund ?? 0;
  const targetAmount = campaign?.campaignDetails?.targetAmount ?? 110000000;
  const raisedSqft = Math.floor(raisedAmount / PRICE_PER_SQFT);
  const goalSqft = Math.max(Math.floor(targetAmount / PRICE_PER_SQFT), 1);
  const sqftProgress = Math.min((raisedSqft / goalSqft) * 100, 100);
  const fundProgress = Number(calculatePercentage(targetAmount, raisedAmount));

  const recentDonors = useMemo(() => {
    const list = campaign?.userList ?? [];
    return list.filter((d) => !d.isanonymous).slice(0, 8);
  }, [campaign]);


  const handleOnChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === "checkbox" ? checked : value });
  };

  const validateForm = () => {
    const newErrors = {};
    const mobileRegex = /^[+]?[0-9]{10,15}$/; // Accept international format
    const pincodeRegex = /^[0-9]{6}$/;
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;

    if (!formData.mobileno) {
      newErrors.mobileno = "Mobile number is required";
    } else if (!mobileRegex.test(formData.mobileno.replace(/\s/g, ''))) {
      newErrors.mobileno = "Please enter a valid mobile number";
    }
    if (!formData.username) {
      newErrors.username = "Full name is required";
    }
    if (formData.taxExemption) {
      if (!formData.panno) {
        newErrors.panno = "PAN number is required for 80G";
      } else if (!panRegex.test(formData.panno)) {
        newErrors.panno = "Please enter a valid PAN number";
      }
    }
    if (formData.prasadRequired) {
      if (!formData.address) newErrors.address = "Address is required for prasad";
      if (!formData.pincode) {
        newErrors.pincode = "Pincode is required";
      } else if (!pincodeRegex.test(formData.pincode)) {
        newErrors.pincode = "Please enter a valid 6-digit pincode";
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    console.log("Form submitted", formData);
    
    // Calculate amount based on selected square feet
    const sqft = Number(isCustomMode ? customSqft : selectedQuickSqft);
    console.log("Square feet:", sqft, "Amount:", sqft * PRICE_PER_SQFT);
    
    if (!sqft || sqft < 1) {
      console.log("Invalid square feet");
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      amount: sqft * PRICE_PER_SQFT
    }));
    
    const isValid = validateForm();
    console.log("Form valid:", isValid, "Errors:", errors);
    
    if (!isValid) return;

    console.log("Dispatching payment form data");
    dispatch(postPaymentFormData({
      ...formData,
      amount: sqft * PRICE_PER_SQFT
    })).then((res) => {
      console.log("Payment response:", res);
      const orderId = res?.payload?.order?.id;
      if (orderId) {
        const params = new URLSearchParams({
          name: formData.username || "",
          phone: formData.mobileno || "",
          email: formData.email || "",
          seva: "Square Feet Seva",
          address: formData.address || "",
          pan_number: formData.panno || "",
          send_confirmation_message_to_preacher: `${campaign?.campaignDetails?.preacherName || ""} - ${campaign?.campaignDetails?.phoneno || ""}`,
          campaignsid: String(CAMPAIGN_ID),
          preacher_name: campaign?.campaignDetails?.preacherName || "",
          isanonymous: formData.isanonymous || false,
          campainerName: campaign?.campaignDetails?.campaignName || "Square Feet Seva",
        });
        console.log("Navigating to payment with order ID:", orderId);
        navigate(`/payment/${orderId}?${params.toString()}`);
      } else {
        console.error("No order ID in response");
      }
    }).catch((err) => {
      console.error("Payment form error:", err);
    });
  };

  const scrollToTiers = () => {
    document.getElementById("donate-section")?.scrollIntoView({ behavior: "smooth" });
  };

  const handleCopy = (text, item) => {
    navigator.clipboard.writeText(text);
    setCopiedItem(item);
    setTimeout(() => setCopiedItem(null), 3000);
  };

  const constructionImages = [update1, update2, update3, update4, update5, update6];
  const donorImages = [donor1, donor2, donor3, donor4, donor5, donor1, donor2, donor3, donor4, donor5];

  if (isLoading) {
    return (
      <Flex minH="100vh" bg={palette.pageBg} align="center" justify="center">
        <VStack spacing={4}>
          <Spinner size="xl" color={palette.saffron} thickness="3px" />
          <Text color={palette.textMuted} fontSize="lg">
            Loading Square Feet Seva…
          </Text>
        </VStack>
      </Flex>
    );
  }


  return (
    <Box bg={palette.pageBg} minH="100vh" color={palette.text} pb={isMobile ? "88px" : 0}>
      <Box h="5px" bgGradient="linear(to-r, #B8860B, #E8751A, #B8860B)" />

      {/* Top bar */}
      <Box bg={palette.surface} borderBottom="1px solid" borderColor={palette.border} position="sticky" top={0} zIndex={20}>
        <Container maxW="1400px" py={3}>
          <Flex align="center" justify="space-between" gap={4}>
            <Link to="https://iskconmangalore.org">
              <HStack spacing={3}>
                <Image src={logo} alt="ISKCON Mangalore" width="120px" height="50px" borderRadius="md" border="2px solid" borderColor={palette.goldLight} />
                <VStack align="flex-start" spacing={0} display={{ base: "none", sm: "flex" }}>
                  <Text fontWeight="bold" fontSize="sm" lineHeight="short">
                    ISKCON Mangalore
                  </Text>
                  <Text fontSize="xs" color={palette.textMuted}>
                    Govardhana Hills
                  </Text>
                </VStack>
              </HStack>
            </Link>
            <HStack spacing={2}>

              <Button
                size="sm"
                bg={palette.saffron}
                color="white"
                _hover={{ bg: palette.saffronDark }}
                onClick={scrollToTiers}
                fontWeight="bold"
              >
                Sponsor Sq Ft
              </Button>
            </HStack>
          </Flex>
        </Container>
      </Box>

      {/* Hero */}
      <Box bgGradient={palette.heroGradient} position="relative" overflow="hidden">
        <Box
          position="absolute"
          inset={0}
          opacity={0.4}
          bgImage="radial-gradient(circle at 20% 30%, rgba(232,117,26,0.12) 0%, transparent 50%), radial-gradient(circle at 80% 70%, rgba(184,134,11,0.1) 0%, transparent 45%)"
        />
        <Container maxW="1400px" py={{ base: 10, md: 16 }} position="relative">
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={10} alignItems="center">
            <MotionBox initial={{ opacity: 0, x: -24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
              <Badge
                bg={palette.goldLight}
                color={palette.maroon}
                px={4}
                py={1.5}
                borderRadius="full"
                mb={4}
                fontWeight="bold"
                letterSpacing="wider"
                fontSize="xs"
              >
                SACRED CONSTRUCTION SEVA
              </Badge>
              <Heading
                as="h1"
                fontSize={{ base: "2.2rem", md: "3.25rem", lg: "3.75rem" }}
                lineHeight="1.15"
                mb={4}
                fontWeight="extrabold"
              >
                Sponsor a Square Foot of{" "}
                <Text as="span" color={palette.saffron}>
                  Lord Krishna&apos;s Temple
                </Text>
              </Heading>
              <Text fontSize={{ base: "md", md: "lg" }} color={palette.textMuted} mb={6} maxW="540px">
                Join devotees worldwide in building the Sri Radha Krishna Temple at Govardhana Hills, Mangalore. Every square foot you offer
                becomes part of Vaikuntha on earth.
              </Text>
              <HStack color={palette.textMuted} mb={8} fontSize="sm">
                <FaLocationDot color={palette.saffron} />
                <Text>Benjanapadavu, Mangalore, Karnataka</Text>
              </HStack>
              <Flex wrap="wrap" gap={3}>
                <Button size="lg" bg={palette.saffron} color="white" px={8} _hover={{ bg: palette.saffronDark }} onClick={scrollToTiers}>
                  Start Your Seva — ₹{PRICE_PER_SQFT.toLocaleString("en-IN")}/sq ft
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  borderColor={palette.saffron}
                  color={palette.saffron}
                  _hover={{ bg: "orange.50" }}
                  onClick={() => document.getElementById("campaign-video")?.scrollIntoView({ behavior: "smooth" })}
                >
                  Watch Vision
                </Button>
              </Flex>
            </MotionBox>

            <MotionBox initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}>
              <Box borderRadius="2xl" overflow="hidden" boxShadow="0 24px 48px rgba(44, 24, 16, 0.12)" border="1px solid" borderColor={palette.border}>
                <Image src={project} alt="QR Code" w="full" h="full" objectFit="contain" />
              </Box>
            </MotionBox>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Progress — sq ft + funds */}
      <Box bg={palette.surface} py={{ base: 10, md: 14 }} borderY="1px solid" borderColor={palette.border}>
        <Container maxW="1400px">
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={10}>
            <Box>
              <Text fontSize="sm" fontWeight="bold" color={palette.saffron} letterSpacing="wide" mb={2}>
                SQUARE FEET RAISED
              </Text>
              <Heading size="2xl" mb={2}>
                <CountUp end={raisedSqft} duration={1.8} separator="," />
                <Text as="span" fontSize="lg" color={palette.textMuted} fontWeight="normal">
                  {" "}
                  / {goalSqft.toLocaleString("en-IN")} sq ft goal
                </Text>
              </Heading>
              <Progress value={sqftProgress} size="lg" borderRadius="full" colorScheme="orange" bg={palette.surfaceMuted} mb={3} />
              <Text color={palette.textMuted} fontSize="sm">
                {sqftProgress.toFixed(1)}% of square feet goal · ₹{PRICE_PER_SQFT.toLocaleString("en-IN")} per sq ft
              </Text>
            </Box>
            <SimpleGrid columns={2} spacing={4}>
              {[
                { label: "Funds raised", value: formatCurrency(raisedAmount) },
                { label: "Campaign goal", value: formatCurrency(targetAmount) },
                { label: "Devotees joined", value: (campaign?.totalFunders ?? 0).toLocaleString("en-IN") },
                { label: "Fund progress", value: `${fundProgress}%` },
              ].map((stat) => (
                <Box key={stat.label} p={5} bg={palette.surfaceMuted} borderRadius="xl" border="1px solid" borderColor={palette.border}>
                  <Text fontSize="xs" color={palette.textMuted} mb={1} textTransform="uppercase" letterSpacing="wide">
                    {stat.label}
                  </Text>
                  <Text fontWeight="bold" fontSize={{ base: "lg", md: "xl" }} color={palette.maroon}>
                    {stat.value}
                  </Text>
                </Box>
              ))}
            </SimpleGrid>
          </SimpleGrid>
        </Container>
      </Box>



      {/* Quick donate */}
      <Box id="donate-section" py={{ base: 10, md: 16 }}>
        <Container maxW="1400px">
          <MotionBox initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} textAlign="center" mb={10}>
            <Badge bg={palette.goldLight} color={palette.maroon} px={4} py={1} borderRadius="full" mb={3}>
              CHOOSE YOUR SEVA
            </Badge>
            <Heading size="xl" mb={3}>
              Donate for Mandir Nirman Seva
            </Heading>
            <Text color={palette.textMuted} maxW="640px" mx="auto">
              Sponsor square feet of the sacred temple construction
            </Text>
          </MotionBox>

          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={8} gap={8}>
            {/* Left side - Form */}
            <Box bg={palette.surface} p={{ base: 5, md: 8 }} borderRadius="2xl" border="1px solid" borderColor={palette.border} boxShadow="0 12px 40px rgba(44,24,16,0.06)">
              <form onSubmit={handleSubmit}>
                <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={6}>
                  <Box>
                    <Text fontSize="xs" mb={2} color={palette.textMuted}>
                      Number of Square Feet
                    </Text>
                    <NumberInput min={1} value={isCustomMode ? customSqft : selectedQuickSqft} onChange={(v) => {
                      setCustomSqft(v);
                      setIsCustomMode(true);
                    }}>
                      <NumberInputField bg={palette.surfaceMuted} borderColor={palette.border} h="48px" />
                    </NumberInput>
                  </Box>
                  <Box>
                    <Text fontSize="xs" mb={2} color={palette.textMuted}>
                      Amount
                    </Text>
                    <Input
                      value={formatCurrency(Number(isCustomMode ? customSqft : selectedQuickSqft) * PRICE_PER_SQFT)}
                      isDisabled
                      bg={palette.surfaceMuted}
                      borderColor={palette.border}
                      h="48px"
                      fontWeight="bold"
                    />
                  </Box>
                </SimpleGrid>
                
                <Flex wrap="wrap" gap={2} mt={4} justify="space-between">
                  {[108, 51, 21, 11, 5, 3, 1].map((sqft) => (
                    <Button
                      key={sqft}
                      variant={selectedQuickSqft === sqft && !isCustomMode ? "solid" : "outline"}
                      bg={selectedQuickSqft === sqft && !isCustomMode ? palette.saffron : "transparent"}
                      color={selectedQuickSqft === sqft && !isCustomMode ? "white" : palette.text}
                      borderColor={palette.saffron}
                      _hover={{ borderColor: palette.saffron }}
                      onClick={() => {
                        setSelectedQuickSqft(sqft);
                        setIsCustomMode(false);
                      }}
                      fontSize="sm"
                      fontWeight="semibold"
                      p={1}
                      borderRadius="7px"
                      minW="5rem"
                      maxW="5rem"
                      type="button"
                    >
                      {sqft}
                    </Button>
                  ))}
                </Flex>

                {/* Form fields */}
                <Box mt={6} bg={palette.surfaceMuted} p={6} borderRadius="2xl">
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <Box>
                    <Text fontSize="xs" mb={2} color={palette.textMuted}>
                      Full Name
                    </Text>
                    <Input
                      value={formData.username}
                      onChange={(e) => handleOnChange(e)}
                      name="username"
                      bg={palette.surface}
                      borderColor={errors.username ? "red.500" : "transparent"}
                      borderBottom="2px solid"
                      borderBottomColor={errors.username ? "red.500" : "transparent"}
                      _focus={{ borderBottomColor: palette.saffron }}
                      h="40px"
                    />
                    {errors.username && <Text fontSize="xs" color="red.500" mt={1}>{errors.username}</Text>}
                  </Box>
                  <Box>
                    <Text fontSize="xs" mb={2} color={palette.textMuted}>
                      WhatsApp Number
                    </Text>
                    <Input
                      value={formData.mobileno || "+91 "}
                      onChange={(e) => handleOnChange(e)}
                      name="mobileno"
                      bg={palette.surface}
                      borderColor={errors.mobileno ? "red.500" : "transparent"}
                      borderBottom="2px solid"
                      borderBottomColor={errors.mobileno ? "red.500" : "transparent"}
                      _focus={{ borderBottomColor: palette.saffron }}
                      h="40px"
                    />
                    {errors.mobileno && <Text fontSize="xs" color="red.500" mt={1}>{errors.mobileno}</Text>}
                    <Text fontSize="xs" mt={1} color={palette.textMuted} opacity={0.7}>
                      Your donation receipt and updates will be sent on this number.
                    </Text>
                  </Box>
                </SimpleGrid>

                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mt={4}>
                  <Box>
                    <Text fontSize="xs" mb={2} color={palette.textMuted}>
                      Address Line 1
                    </Text>
                    <Input
                      value={formData.address}
                      onChange={(e) => handleOnChange(e)}
                      name="address"
                      bg={palette.surface}
                      borderColor={errors.address ? "red.500" : "transparent"}
                      borderBottom="2px solid"
                      borderBottomColor={errors.address ? "red.500" : "transparent"}
                      _focus={{ borderBottomColor: palette.saffron }}
                      h="40px"
                    />
                    {errors.address && <Text fontSize="xs" color="red.500" mt={1}>{errors.address}</Text>}
                  </Box>
                  <Box>
                    <Text fontSize="xs" mb={2} color={palette.textMuted}>
                      City
                    </Text>
                    <Input
                      placeholder="City"
                      bg={palette.surface}
                      borderColor="transparent"
                      borderBottom="2px solid"
                      borderBottomColor="transparent"
                      _focus={{ borderBottomColor: palette.saffron }}
                      h="40px"
                    />
                  </Box>
                  <Box>
                    <Text fontSize="xs" mb={2} color={palette.textMuted}>
                      State
                    </Text>
                    <Input
                      placeholder="State"
                      bg={palette.surface}
                      borderColor="transparent"
                      borderBottom="2px solid"
                      borderBottomColor="transparent"
                      _focus={{ borderBottomColor: palette.saffron }}
                      h="40px"
                    />
                  </Box>
                  <Box>
                    <Text fontSize="xs" mb={2} color={palette.textMuted}>
                      Pincode
                    </Text>
                    <Input
                      value={formData.pincode}
                      onChange={(e) => handleOnChange(e)}
                      name="pincode"
                      bg={palette.surface}
                      borderColor={errors.pincode ? "red.500" : "transparent"}
                      borderBottom="2px solid"
                      borderBottomColor={errors.pincode ? "red.500" : "transparent"}
                      _focus={{ borderBottomColor: palette.saffron }}
                      h="40px"
                    />
                    {errors.pincode && <Text fontSize="xs" color="red.500" mt={1}>{errors.pincode}</Text>}
                  </Box>
                </SimpleGrid>

                {/* Checkboxes */}
                <VStack align="start" spacing={3} mt={4}>
                  <Checkbox
                    isChecked={formData.taxExemption}
                    onChange={(e) => handleOnChange(e)}
                    name="taxExemption"
                    colorScheme="orange"
                  >
                    I would like to avail tax benefits (80G)
                  </Checkbox>
                  {formData.taxExemption && (
                    <Box w="full" mt={2}>
                      <Text fontSize="xs" mb={2} color={palette.textMuted}>
                        PAN Number
                      </Text>
                      <Input
                        value={formData.panno}
                        onChange={(e) => handleOnChange(e)}
                        name="panno"
                        bg={palette.surface}
                        borderColor={errors.panno ? "red.500" : "transparent"}
                        borderBottom="2px solid"
                        borderBottomColor={errors.panno ? "red.500" : "transparent"}
                        _focus={{ borderBottomColor: palette.saffron }}
                        h="40px"
                        textTransform="uppercase"
                      />
                      {errors.panno && <Text fontSize="xs" color="red.500" mt={1}>{errors.panno}</Text>}
                    </Box>
                  )}
                  <Checkbox
                    isChecked={formData.prasadRequired}
                    onChange={(e) => handleOnChange(e)}
                    name="prasadRequired"
                    colorScheme="orange"
                  >
                    I would like to receive Maha Prasadam (Only within India)
                  </Checkbox>
                  <Checkbox colorScheme="orange">
                    I would like to receive Sankalp and Aarti video on my birthday
                  </Checkbox>
                </VStack>
              </Box>

              <Button
                w="40%"
                bg={palette.saffron}
                color="white"
                _hover={{ bg: palette.saffronDark }}
                type="submit"
                ml="auto"
                mt={4}
              >
                Donate
              </Button>
              </form>
            </Box>

            {/* Right side - Payment Info */}
            <VStack spacing={4} align="start">
              <Box bg={palette.surfaceMuted} p={6} borderRadius="2xl" w="full">
                <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
                  <Box textAlign="center">
                    <Heading size="sm" mb={4} color={palette.maroon}>
                      For UPI & QR
                    </Heading>
                    <Box maxW="192px" mx="auto" bg={palette.saffron} borderRadius="2xl" p={2} aspectRatio={1} mb={2}>
                      <Box bg="white" borderRadius="lg" p={2} w="full" h="full">
                        <Image src={qrCode} alt="QR Code" w="full" h="full" objectFit="contain" />
                      </Box>
                    </Box>
                    <Text fontWeight="bold" fontSize="sm" textAlign="center">
                      seva.augp@aubank
                    </Text>
                  </Box>
                  <Box>
                    <Heading size="sm" mb={4} color={palette.maroon}>
                      For Bank Transfer
                    </Heading>
                    <VStack align="start" spacing={2} fontSize="sm">
                      <HStack>
                        <Text color="gray.600">Account Name:</Text>
                        <Text fontWeight="bold" color={palette.saffron}>ISKCON Mangalore</Text>
                        <Icon 
                          as={copiedItem === "accountName" ? FaCheck : FaCopy} 
                          color={copiedItem === "accountName" ? "green.500" : palette.saffron} 
                          cursor="pointer" 
                          onClick={() => handleCopy("ISKCON Mangalore", "accountName")} 
                        />
                      </HStack>
                      <HStack>
                        <Text color="gray.600">Account No:</Text>
                        <Text fontWeight="bold" color={palette.saffron}>520401000006630</Text>
                        <Icon 
                          as={copiedItem === "accountNumber" ? FaCheck : FaCopy} 
                          color={copiedItem === "accountNumber" ? "green.500" : palette.saffron} 
                          cursor="pointer" 
                          onClick={() => handleCopy("520401000006630", "accountNumber")} 
                        />
                      </HStack>
                      <HStack>
                        <Text color="gray.600">Bank Name:</Text>
                        <Text fontWeight="bold" color={palette.saffron}>Union Bank of India</Text>
                        <Icon 
                          as={copiedItem === "bankName" ? FaCheck : FaCopy} 
                          color={copiedItem === "bankName" ? "green.500" : palette.saffron} 
                          cursor="pointer" 
                          onClick={() => handleCopy("Union Bank of India", "bankName")} 
                        />
                      </HStack>
                      <HStack>
                        <Text color="gray.600">IFSC Code:</Text>
                        <Text fontWeight="bold" color={palette.saffron}>UBIN0905968</Text>
                        <Icon 
                          as={copiedItem === "ifscCode" ? FaCheck : FaCopy} 
                          color={copiedItem === "ifscCode" ? "green.500" : palette.saffron} 
                          cursor="pointer" 
                          onClick={() => handleCopy("UBIN0905968", "ifscCode")} 
                        />
                      </HStack>
                      <HStack>
                        <Text color="gray.600">Branch</Text>
                        <Text fontWeight="bold" color={palette.saffron}>Mangalore - Kadri</Text>
                        <Icon 
                          as={copiedItem === "branch" ? FaCheck : FaCopy} 
                          color={copiedItem === "branch" ? "green.500" : palette.saffron} 
                          cursor="pointer" 
                          onClick={() => handleCopy("Mangalore - Kadri", "branch")} 
                        />
                      </HStack>
                    </VStack>
                  </Box>
                </SimpleGrid>
                <Text textAlign="center" fontSize="xs" fontWeight="bold" color={palette.saffron} mt={4}>
                  (Kindly send us a screenshot for your seva entry)
                </Text>

                <Box mt={6} pt={6} borderTop="1px solid" borderColor={palette.border}>
                  <Text fontSize="sm" fontWeight="bold">
                    *80G available as per Income Tax Act 1961 and rules made there under.
                  </Text>
                  <Text fontSize="sm" fontWeight="bold" mt={2}>
                    *By proceeding, you are agreeing to our Terms & Conditions & Privacy Policy
                  </Text>

                  <Box mt={4}>
                    <Heading size="sm" mb={2}>Support</Heading>
                    <Text fontSize="sm">For more information please contact:</Text>
{/*                     <HStack mt={2} mb={3} spacing={2}> */}
{/*                       <Button size="xs" bg={palette.goldLight} color={palette.maroon} borderRadius="full"> */}
{/*                         Tax Benefits */}
{/*                       </Button> */}
{/*                       <Button size="xs" bg={palette.goldLight} color={palette.maroon} borderRadius="full"> */}
{/*                         Assured */}
{/*                       </Button> */}
{/*                     </HStack> */}
                    <HStack spacing={4}>
                      <HStack as="a" href="https://wa.me/" target="_blank" spacing={2}>
                        <Box p={2} bg={palette.surface} borderRadius="xl">
                          <FaWhatsapp color={palette.saffron} />
                        </Box>
                        <Text fontSize="sm" fontWeight="bold">+91 7259206910</Text>
                      </HStack>
                      <HStack spacing={2}>
                        <Box p={2} bg={palette.surface} borderRadius="xl">
                          <Text color={palette.saffron}>✉</Text>
                        </Box>
                        <Text fontSize="sm" fontWeight="bold">dcc@iskconmangalore.org</Text>
                      </HStack>
                    </HStack>
                  </Box>
                </Box>
              </Box>
            </VStack>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Privileges + photo gallery */}
      <Box bg={palette.surfaceMuted} py={{ base: 10, md: 16 }}>
        <Container maxW="1400px">
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={12} alignItems="center">
            <Box>
              <Heading size="lg" mb={4}>
                Donor privileges
              </Heading>
              <Text color={palette.textMuted} mb={6}>
                Each square foot supporter receives spiritual recognition and updates as the mandir rises—similar to VCM and ISKCON Mysore square-feet
                programs.
              </Text>
              <SimpleGrid columns={2} spacing={4}>
                {[
                  "Prasadam & blessings",
                  "Wall of Gratitude",
                  "Construction updates",
                  "80G tax receipt",
                  "Festival invitations",
                  "Dedicated in loved ones' names",
                ].map((item) => (
                  <HStack key={item} align="start">
                    <Box mt={1} boxSize={2} borderRadius="full" bg={palette.saffron} flexShrink={0} />
                    <Text fontSize="sm">{item}</Text>
                  </HStack>
                ))}
              </SimpleGrid>
            </Box>
            <Box position="relative" overflow="hidden" borderRadius="2xl" boxShadow="0 8px 32px rgba(44,24,16,0.06)">
              <Swiper
                slidesPerView={1}
                spaceBetween={16}
                loop={true}
                autoplay={{
                  delay: 3000,
                  disableOnInteraction: false,
                }}
                navigation={true}
                modules={[Navigation, Autoplay]}
                style={{
                  paddingBottom: '0',
                }}
              >
                {donorImages.map((img, index) => (
                  <SwiperSlide key={index}>
                    <Box
                      borderRadius="2xl"
                      overflow="hidden"
                      aspectRatio={16/9}
                      maxW="400px"
                      mx="auto"
                    >
                      <Image
                        src={img}
                        alt={`Donor privilege ${index + 1}`}
                        w="full"
                        h="full"
                        objectFit="contain"
                      />
                    </Box>
                  </SwiperSlide>
                ))}
              </Swiper>
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Video */}
      <Box py={{ base: 10, md: 16 }} id="campaign-video">
        <Container maxW="1400px">
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={10} alignItems="center">
            <Box
              borderRadius="2xl"
              overflow="hidden"
              boxShadow="0 8px 32px rgba(44,24,16,0.1)"
              bg="black"
            >
              <Box
                as="iframe"
                width="100%"
                aspectRatio={16/9}
                src="https://www.youtube.com/embed/n8iQrsaMvx8"
                title="ISKCON Mangalore Govardhan Hills - Temple Construction Update (Feb 2026)"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            </Box>
            <VStack align="start" spacing={4}>
              <Heading size="lg">Witness the vision</Heading>
              <Text color={palette.textMuted}>
                Watch the construction update of the temple at Govardhana Hills. Devotees connect emotionally when they see steel, stone, and shikhara
                rising at this sacred location.
              </Text>
              <Text color={palette.textMuted}>
                The 11-acre campus will host Sri Sri Radha Krishna, cultural education, gaushala, guest facilities, and festivals for coastal Karnataka.
              </Text>
              <Button bg={palette.saffron} color="white" _hover={{ bg: palette.saffronDark }} onClick={scrollToTiers}>
                Contribute today
              </Button>
            </VStack>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Gallery */}
      <Box bg={palette.surface} py={{ base: 10, md: 16 }} borderY="1px solid" borderColor={palette.border}>
        <Container maxW="1400px">
          <Heading textAlign="center" mb={2}>
            Temple Construction Status - As on April 2026
          </Heading>
          <Text textAlign="center" color={palette.textMuted} mb={10}>
            Witness the progress of temple construction
          </Text>
          
          <Box maxW="1000px" mx="auto">
            <Swiper
              effect="coverflow"
              grabCursor={true}
              centeredSlides={true}
              slidesPerView="auto"
              coverflowEffect={{
                rotate: 0,
                stretch: 0,
                depth: 1000,
                modifier: 1,
                slideShadows: true,
              }}
              autoplay={{
                delay: 3000,
                disableOnInteraction: false,
              }}
              pagination={{
                clickable: true,
                dynamicBullets: true,
              }}
              navigation={true}
              modules={[EffectCoverflow, Autoplay, Pagination, Navigation]}
              style={{
                paddingBottom: '50px',
              }}
            >
              {constructionImages.map((img, index) => (
                <SwiperSlide key={index}>
                  <Box
                    borderRadius="2xl"
                    overflow="hidden"
                    boxShadow="0 12px 40px rgba(44,24,16,0.1)"
                    aspectRatio={16/9}
                    maxW="600px"
                    mx="auto"
                  >
                    <Image
                      src={img}
                      alt={`Construction update ${index + 1}`}
                      w="full"
                      h="full"
                      objectFit="cover"
                    />
                  </Box>
                </SwiperSlide>
              ))}
            </Swiper>
          </Box>
        </Container>
      </Box>

      {/* Recent supporters */}
      {recentDonors.length > 0 && (
        <Box py={{ base: 10, md: 14 }}>
          <Container maxW="1400px">
            <Heading size="md" mb={6} textAlign="center">
              Recent square-feet supporters
            </Heading>
            <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing={4}>
              {recentDonors.map((donor, i) => (
                <Box key={`${donor.username}-${i}`} p={4} bg={palette.surface} borderRadius="xl" border="1px solid" borderColor={palette.border}>
                  <Text fontWeight="bold" noOfLines={1}>
                    {donor.username}
                  </Text>
                  <Text color={palette.saffron} fontWeight="semibold">
                    {formatCurrency(donor.amount)}
                  </Text>
                  <Text fontSize="xs" color={palette.textMuted}>
                    ~{Math.floor(donor.amount / PRICE_PER_SQFT)} sq ft seva
                  </Text>
                </Box>
              ))}
            </SimpleGrid>
          </Container>
        </Box>
      )}
        {/* Trust strip */}
              <Container maxW="full" py={6}>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                  {[
                    { icon: FaReceipt, title: "80G Tax Exemption", desc: "Valid receipt when PAN is provided" },
                    { icon: FaShieldAlt, title: "Secure Payments", desc: "Razorpay-powered checkout" },
                    { icon: FaHeart, title: "Transparent Seva", desc: "Regular construction updates" },
                  ].map((item) => (
                    <HStack key={item.title} p={4} bg={palette.surface} borderRadius="xl" border="1px solid" borderColor={palette.border} spacing={4}>
                      <Flex boxSize="48px" borderRadius="lg" bg={palette.goldLight} align="center" justify="center">
                        <Icon as={item.icon} color={palette.maroon} />
                      </Flex>
                      <Box>
                        <Text fontWeight="bold" fontSize="sm">
                          {item.title}
                        </Text>
                        <Text fontSize="xs" color={palette.textMuted}>
                          {item.desc}
                        </Text>
                      </Box>
                    </HStack>
                  ))}
                </SimpleGrid>
              </Container>
      {/* How it works */}
      <Box bg={palette.surfaceMuted} py={{ base: 10, md: 14 }}>
        <Container maxW="1400px">
          <Heading textAlign="center" mb={10}>
            How it works
          </Heading>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
            {[
              { step: "1", title: "Choose square feet", desc: "Pick a tier or enter custom sq ft at ₹2,100 each." },
              { step: "2", title: "Complete details", desc: "Secure checkout with optional 80G and prasad delivery." },
              { step: "3", title: "Receive blessings", desc: "Get receipt, recognition, and ongoing temple updates." },
            ].map((item) => (
              <VStack key={item.step} p={6} bg={palette.surface} borderRadius="2xl" border="1px solid" borderColor={palette.border} spacing={3}>
                <Flex boxSize="40px" borderRadius="full" bg={palette.saffron} color="white" fontWeight="bold" align="center" justify="center">
                  {item.step}
                </Flex>
                <Text fontWeight="bold">{item.title}</Text>
                <Text fontSize="sm" color={palette.textMuted} textAlign="center">
                  {item.desc}
                </Text>
              </VStack>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* FAQ */}
      <Box py={{ base: 10, md: 16 }}>
        <Container maxW="800px">
          <Heading textAlign="center" mb={8}>
            Frequently asked questions
          </Heading>
          <Accordion allowMultiple>
            {faqs.map((faq, index) => (
              <AccordionItem key={index} mb={3} border="1px solid" borderColor={palette.border} borderRadius="lg" overflow="hidden" bg={palette.surface}>
                <AccordionButton py={5} px={5} _hover={{ bg: palette.surfaceMuted }}>
                  <Box flex="1" textAlign="left" fontWeight="semibold">
                    {faq.question}
                  </Box>
                  <AccordionIcon color={palette.saffron} />
                </AccordionButton>
                <AccordionPanel pb={5} px={5} color={palette.textMuted}>
                  {faq.answer}
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>
        </Container>
      </Box>

      {/* Final CTA */}
      <Box bgGradient="linear(135deg, #7B2D26 0%, #9B3D35 50%, #E8751A 100%)" py={{ base: 12, md: 16 }} color="white">
        <Container maxW="800px" textAlign="center">
          <Heading size="xl" mb={4}>
            Your square foot. Their eternal home.
          </Heading>
          <Text mb={8} opacity={0.95}>
            Be among the builders of coastal Karnataka&apos;s grandest spiritual landmark. Campaign ID {CAMPAIGN_ID} · Square Feet Seva.
          </Text>
          <Button size="lg" bg="white" color={palette.maroon} px={10} _hover={{ bg: palette.goldLight }} onClick={scrollToTiers}>
            Sponsor your square foot
          </Button>
        </Container>
      </Box>

      <Box h="5px" bgGradient="linear(to-r, #B8860B, #E8751A, #B8860B)" />

      {paymentModel && (
        <PaymentModal
          themeColor={themeColor}
          amount={amount}
          setPaymentModel={setPaymentModel}
          handleSubmit={handleSubmit}
          handleOnChange={handleOnChange}
          formData={formData}
          errors={errors}
        />
      )}

      {showScrollButton && (
        <IconButton
          icon={<FaArrowUp />}
          position="fixed"
          bottom={isMobile ? "96px" : "24px"}
          right="20px"
          zIndex={15}
          aria-label="Scroll to top"
          bg={palette.surface}
          border="1px solid"
          borderColor={palette.border}
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        />
      )}

      {isMobile && (
        <Flex
          position="fixed"
          bottom={0}
          left={0}
          right={0}
          zIndex={15}
          bg={palette.surface}
          borderTop="1px solid"
          borderColor={palette.border}
          p={3}
          gap={2}
          boxShadow="0 -8px 24px rgba(44,24,16,0.08)"
        >
          <Button flex={1} variant="outline" borderColor={palette.saffron} color={palette.saffron} leftIcon={<FaWhatsapp />} as="a" href="https://wa.me/" target="_blank" rel="noopener noreferrer">
            Share
          </Button>
          <Button flex={2} bg={palette.saffron} color="white" _hover={{ bg: palette.saffronDark }} onClick={scrollToTiers}>
            Donate · from ₹2,100
          </Button>
        </Flex>
      )}

      <Fotter />
    </Box>
  );
};

export default SqftSevaCampaign;
